import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Image from '../AppImage';
import MentionTextarea from './MentionTextarea';
import authService from '../../utils/authService';
import realApiService from '../../utils/realApiService';
import useToast from '../../hooks/useToast';
import InitialsBadge from './InitialsBadge';

const ActivityTimeline = ({ card, onAddComment, canComment }) => {
  const [newComment, setNewComment] = useState('');
  const [isCommenting, setIsCommenting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { showToast } = useToast();

  // Initials and colors are now handled by InitialsBadge component

  // Load current user and activities on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Get current user
        const user = authService.getCurrentUser();
        setCurrentUser(user);

        // Load card activities including comments
        if (card?.id) {
          try {
            const cardActivities = await realApiService.cards.getCardActivities(card.id);
            setActivities(cardActivities || []);
          } catch (activityError) {
            console.error('Error loading card activities:', activityError);
            // Set empty activities array if API fails
            setActivities([]);
          }
        }
      } catch (error) {
        console.error('Error loading activity data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [card?.id]);

  const handleAddComment = async () => {
    if (newComment.trim() && !submitting && card?.id) {
      try {
        setSubmitting(true);

        const commentData = {
          content: newComment.trim(),
          mentions: extractMentions(newComment)
        };

        // Add comment via API
        const addedComment = await realApiService.cards.addCommentToCard(card.id, commentData);

        // Update local activities state
        setActivities(prev => [...prev, addedComment]);

        // Call parent callback if provided
        if (onAddComment) {
          onAddComment(addedComment);
        }

        setNewComment('');
        setIsCommenting(false);
      } catch (error) {
        console.error('Error adding comment:', error);
        showToast('Failed to add comment. Please try again.', 'error');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const extractMentions = (text) => {
    const mentionRegex = /@(\w+(?:\.\w+)*)/g;
    const mentions = [];
    let match;
    
    while ((match = mentionRegex.exec(text)) !== null) {
      mentions.push(match[1]);
    }
    
    return mentions;
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return timestamp.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: timestamp.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const renderActivityContent = (activity) => {
    if (activity.type === 'comment') {
      // Process mentions in comments
      let content = activity.content;
      if (activity.mentions && activity.mentions.length > 0) {
        activity.mentions.forEach(mention => {
          content = content.replace(
            new RegExp(`@${mention}`, 'g'),
            `<span class="text-primary font-medium">@${mention}</span>`
          );
        });
      }
      
      return (
        <div 
          className="text-text-primary"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    }
    
    return (
      <div className="text-text-secondary">
        <span className="font-medium text-text-primary">{activity.user.name}</span>
        {' '}
        {activity.content}
      </div>
    );
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleAddComment();
    } else if (e.key === 'Escape') {
      setIsCommenting(false);
      setNewComment('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <Icon name="MessageSquare" size={20} className="text-text-secondary" />
        <h3 className="text-lg font-semibold text-text-primary">Activity</h3>
      </div>

      {/* Add Comment Section */}
      {canComment && currentUser && (
        <div className="flex space-x-3">
          {currentUser?.avatar ? (
            <Image
              src={currentUser.avatar}
              alt={currentUser.name || 'User'}
              className="w-10 h-10 rounded-full object-cover flex-shrink-0 shadow-sm ring-1 ring-white/10"
            />
          ) : (
            <InitialsBadge
              name={currentUser?.displayName || currentUser?.name || currentUser?.email || 'U'}
              size="md"
              className="flex-shrink-0"
            />
          )}
          <div className="flex-1">
            {isCommenting ? (
              <div className="space-y-3">
                <MentionTextarea
                  value={newComment}
                  onChange={setNewComment}
                  onKeyDown={handleKeyPress}
                  placeholder="Write a comment... Use @username to mention someone"
                  cardId={card?.id}
                  className="min-h-[80px] focus:ring-2 focus:ring-primary focus:border-transparent"
                  rows={3}
                  disabled={submitting}
                />
                <div className="flex items-center justify-between">
                  <div className="text-xs text-text-secondary">
                    Press Ctrl+Enter to post, Escape to cancel
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      onClick={handleAddComment}
                      disabled={submitting || !newComment.trim()}
                    >
                      {submitting ? 'Posting...' : 'Comment'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIsCommenting(false);
                        setNewComment('');
                      }}
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setIsCommenting(true)}
                className="w-full p-3 text-left text-text-secondary bg-muted rounded-md hover:bg-muted/80 transition-micro"
              >
                Write a comment...
              </button>
            )}
          </div>
        </div>
      )}

      {/* Activity Timeline */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3"></div>
            <p className="text-text-secondary">Loading activity...</p>
          </div>
        ) : (
          <>
            {activities.map((activity) => (
              <div key={activity.id} className="py-2 border-b border-border/50 last:border-b-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-text-primary text-sm">
                    {activity.user?.name || 'Unknown User'}
                  </span>
                  <span className="text-xs text-text-secondary">
                    {formatTimestamp(new Date(activity.timestamp || activity.created_at))}
                  </span>
                </div>
                <div className="text-sm text-text-primary pl-0">
                  {renderActivityContent(activity)}
                </div>
              </div>
            ))}

            {activities.length === 0 && !loading && (
              <div className="text-center py-8 text-text-secondary">
                <Icon name="MessageSquare" size={48} className="mx-auto mb-3 opacity-50" />
                <p>No activity yet</p>
                <p className="text-sm">Comments and updates will appear here</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ActivityTimeline;
